import React, { useContext } from "react"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import { useTelemedicineConsents } from "#/components/telemedicine/hooks/useTelemedicineConsents"
import {
  Box,
  dayjs,
  DialogActions,
  DialogContent,
  DialogTitle,
  ForwardIcon,
  Progress
} from "@carrotfertility/carotene-core"
import { DialogCloseButton, Form, FormButton, FormRadioGroup } from "@carrotfertility/carotene-core-x"
import { FormattedMessage, useIntl } from "react-intl"
import { TelemedicineDialogContext } from "#/components/telemedicine/EnterTelemedicineForMenopauseDialog"
import { FieldValues } from "react-hook-form"

function isWithinAgeRange(dateOfBirth: string): boolean {
  const age = dayjs().diff(dayjs(dateOfBirth), "year")
  return age >= 40 && age <= 75
}

export function WhoWillBeReceivingCareStep(): JSX.Element {
  const { setOpenDialogFlow } = useContext(TelemedicineDialogContext)
  const { data: telemedicineConsentTime, isLoading: telemedicineConsentTimeIsLoading } = useTelemedicineConsents()
  const { isPartnerAccessingAccount } = useUserRole()
  const {
    preferredName,
    firstName,
    partnerPreferredName,
    partnerFirstName,
    dateOfBirth: memberDateOfBirth,
    partnerDateOfBirth
  } = useCurrentUser()
  const intl = useIntl()

  const partnerName = partnerPreferredName ?? partnerFirstName
  const options = [
    { label: preferredName ?? firstName, value: "MEMBER" },
    {
      label: partnerName ?? intl.formatMessage({ defaultMessage: "{firstName}'s partner" }, { firstName }),
      value: "PARTNER"
    }
  ]

  // If the user has already consented to telemedicine then we rely on "isPartnerAccessingAccount" to determine if the current user
  // is the member or the partner and then check if the appropriate person is within the age range.
  // If they are within the age range then we send them to the legal consent step, otherwise we send them to the not eligible step.
  const withinAgeRangeValue = isWithinAgeRange(isPartnerAccessingAccount ? partnerDateOfBirth : memberDateOfBirth)
    ? "LEGAL_CONSENT_SIGNED"
    : "NOT_ELIGIBLE"
  const { send } = useStateMachine(telemedicineConsentTime?.telemedicineConsentTimeUtc ? withinAgeRangeValue : null)

  const onFormSubmit = (formValues: FieldValues) => {
    // If the selection matches the current user then we do the age check.
    // If the selection does not match the current user then we send them to the appropriate member or partner step.
    const currentUserMatchesSelection =
      (isPartnerAccessingAccount && formValues.whoWillBeReceivingCare === "PARTNER") ||
      (!isPartnerAccessingAccount && formValues.whoWillBeReceivingCare === "MEMBER")

    if (currentUserMatchesSelection) {
      const dateOfBirth = formValues.whoWillBeReceivingCare === "MEMBER" ? memberDateOfBirth : partnerDateOfBirth
      send(isWithinAgeRange(dateOfBirth) ? formValues.whoWillBeReceivingCare : "NOT_ELIGIBLE")
    } else {
      send(formValues.whoWillBeReceivingCare)
    }
  }

  return (
    <Form onSubmit={onFormSubmit}>
      <DialogCloseButton onClick={() => setOpenDialogFlow(false)} />
      {telemedicineConsentTimeIsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Progress />
        </Box>
      ) : (
        <>
          <DialogTitle id="who-will-be-receiving-care-title">
            <FormattedMessage defaultMessage="Who will be receiving care?" />
          </DialogTitle>
          <DialogContent>
            <FormRadioGroup
              aria-labelledby="who-will-be-receiving-care-title"
              name="whoWillBeReceivingCare"
              options={isPartnerAccessingAccount ? options.reverse() : options}
              controllerProps={{
                rules: {
                  required: "Required"
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <FormButton type="submit" color="secondary" variant="outlined" endIcon={<ForwardIcon />}>
              <FormattedMessage defaultMessage="Continue" />
            </FormButton>
          </DialogActions>
        </>
      )}
    </Form>
  )
}
